import { NotificationType, useApp, useTranslation, useUserInfo } from "@next-platform/client-common-core";
import { RECOMBEE_DB_NAME, RECOMBEE_PUBLIC_TOKEN, RECOMBEE_REGION } from "@next-platform/client-common-core/infra/constants";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { useCallback } from "react";
import recombee from "recombee-js-api-client";

import { getOfflineNotificationMessage } from "@/components/common/Notification/utils/notification";
import { DEFAULT_HOMEPAGE_RECOMMENDATIONS_NUM, RECOMMENDATION_TIMEOUT_MS_7_DAYS } from "@/constants";
import {
  AddBookmarkOptions,
  AddCartAdditionOptions,
  AddDetailViewOptions,
  AddRatingOptions,
  AddViewPortionOptions,
  IBuildFilterFromParams,
  IGetCachedRecommendationsParams,
  IGetRecommendationsParams,
  IRecommendationValues,
  IRecommItems,
} from "@/hooks/types";
import useToast from "@/hooks/useToast";
import { typedLocalStorageService } from "@/utils/localStorageRe";
import { storeRecommId } from "@/utils/recombeeSessionStorage";

const logger = appLogger.withTag("useRecombee");

const stripNegativeTagPrefix = (tag: string) => tag.slice(1);

const isNegativeTag = (tag: string) => tag.startsWith("-");

const isRecommendationRequestAborted = (signal?: AbortSignal) => signal?.aborted === true;

function buildFilterFromParams({ applicationId, selectedTags = [], nsfwToggle }: IBuildFilterFromParams) {
  const filters = [];
  if (applicationId) {
    filters.push(`"${applicationId}" in 'application_ids'`);
  }

  if (applicationId !== "adulttime_application" && !nsfwToggle) {
    filters.push(`not 'is_nsfw'`);
  }

  //we only keep the negative tags
  const negativeTags = selectedTags.filter(isNegativeTag).map(stripNegativeTagPrefix);
  if (negativeTags.length > 0) {
    filters.push(`(if 'tags' & {"${negativeTags.join('","')}"} then false else true)`);
  }

  return filters.join(" and ");
}

function characterMatchesTag(character: IRecommendationValues, tag: string) {
  if (isNegativeTag(tag)) {
    return !character?.values?.tags.includes(stripNegativeTagPrefix(tag));
  }
  return character?.values?.tags.includes(tag);
}

function filterBlockedCreatorsAndTags(recommItems: IRecommItems, blockedCreators: string[], filteredTags: string[] = []) {
  recommItems.recomms = recommItems?.recomms?.filter((character: IRecommendationValues) => {
    if (blockedCreators.includes(character?.values?.creator_user_id)) {
      return false;
    }
    return filteredTags.every((tag: string) => characterMatchesTag(character, tag));
  });
  return recommItems;
}

function readFreshCachedRecommendations({
  storageKey,
  timeOut,
  userId,
}: {
  storageKey: string;
  timeOut: number;
  userId: string;
}): IRecommItems | null {
  const storedRecommendations = JSON.parse(typedLocalStorageService.getItem(storageKey) as unknown as string);
  // Home's storage key has no viewer id. A guest write would otherwise
  // satisfy a later signed-in read for the whole cache window.
  const isCachedForViewer = storedRecommendations?.userId === userId;
  if (storedRecommendations?.recomms && isCachedForViewer && Date.now() - storedRecommendations?.timestamp < timeOut) {
    return storedRecommendations;
  }
  return null;
}

function storeCachedRecommendations({ storageKey, userId, recommendations }: { storageKey: string; userId: string; recommendations: IRecommItems }) {
  if (!recommendations?.recomms) {
    return;
  }
  typedLocalStorageService.setItem(
    storageKey,
    JSON.stringify({
      ...recommendations,
      timestamp: Date.now(),
      userId,
    }),
  );
}

async function loadCachedRecommendations({
  storageKey,
  timeOut = RECOMMENDATION_TIMEOUT_MS_7_DAYS,
  userId,
  scenario,
  recommendationsCount = DEFAULT_HOMEPAGE_RECOMMENDATIONS_NUM,
  blockedUsers = [],
  applicationId,
  nsfwToggle,
  selectedTags,
  signal,
  fetchRecommendations,
  onError,
}: IGetCachedRecommendationsParams & {
  fetchRecommendations: (params: IGetRecommendationsParams) => Promise<IRecommItems>;
  onError: (error: unknown) => void;
}) {
  if (isRecommendationRequestAborted(signal)) {
    return null;
  }
  try {
    const storedRecommendations = readFreshCachedRecommendations({ storageKey, timeOut, userId });
    if (storedRecommendations) {
      return filterBlockedCreatorsAndTags(storedRecommendations, blockedUsers, selectedTags);
    }
    const filter = buildFilterFromParams({ applicationId, selectedTags, nsfwToggle });
    const fetchedRecommendations = await fetchRecommendations({
      userId,
      filter,
      scenario,
      recommendationsCount,
    });
    // A viewer-id change aborts Discovery while this Recombee call is still
    // in flight. Writing afterwards would replace the signed-in entry that
    // already landed in Home's shared key, and the next read would miss.
    if (isRecommendationRequestAborted(signal)) {
      return null;
    }
    storeCachedRecommendations({ storageKey, userId, recommendations: fetchedRecommendations });
    return filterBlockedCreatorsAndTags(fetchedRecommendations, blockedUsers, selectedTags);
  } catch (error) {
    onError(error);
  }
  return null;
}

const client = new recombee.ApiClient(RECOMBEE_DB_NAME as string, RECOMBEE_PUBLIC_TOKEN as string, {
  region: RECOMBEE_REGION as string,
});

async function sendRecombeeRequest(request: Parameters<typeof client.send>[0], onError: (error: unknown) => void) {
  try {
    await client.send(request);
  } catch (error) {
    onError(error);
  }
}

export function useRecombee() {
  const { onlyUserId, guestUserId } = useUserInfo();
  const recombeeUserId = onlyUserId || "gid_" + guestUserId;
  const { displayToast } = useToast();
  const { t } = useTranslation();
  const { typesenseConfig } = useApp();

  const errorHandler = useCallback(
    (error: unknown) => {
      if (!navigator.onLine) {
        displayToast({ message: getOfflineNotificationMessage(t), type: NotificationType.warning });
      }
      logger.error("Error in errorHandler", error);
    },
    [displayToast, t],
  );

  const sendUserInteractionDetailView = useCallback(
    (userId: string, characterId: string, options?: AddDetailViewOptions) => {
      if (options?.recommId && characterId) {
        storeRecommId(characterId, options.recommId);
      }
      return sendRecombeeRequest(new recombee.AddDetailView(userId, characterId, options), errorHandler);
    },
    [errorHandler],
  );

  const sendUserInteractionCartAddition = useCallback(
    (userId: string, characterId: string, options?: AddCartAdditionOptions) =>
      sendRecombeeRequest(new recombee.AddCartAddition(userId, characterId, options), errorHandler),
    [errorHandler],
  );

  const sendUserInteractionAddBookmark = useCallback(
    (userId: string, characterId: string, options?: AddBookmarkOptions) =>
      sendRecombeeRequest(new recombee.AddBookmark(userId, characterId, options), errorHandler),
    [errorHandler],
  );

  const sendUserInteractionRating = useCallback(
    (userId: string, characterId: string, rating: number, options?: AddRatingOptions) =>
      sendRecombeeRequest(new recombee.AddRating(userId, characterId, rating, options), errorHandler),
    [errorHandler],
  );

  const sendUserInteractionViewPortion = useCallback(
    (userId: string, characterId: string, portion: number, options?: AddViewPortionOptions) =>
      sendRecombeeRequest(new recombee.SetViewPortion(userId, characterId, portion, options), errorHandler),
    [errorHandler],
  );

  const getRecommendations = useCallback(
    async ({
      userId,
      filter = undefined,
      scenario,
      recommendationsCount = DEFAULT_HOMEPAGE_RECOMMENDATIONS_NUM,
      returnProperties = true,
    }: IGetRecommendationsParams): Promise<IRecommItems> => {
      // E2E: Recombee is a single shared DB across every e2e env, so it can't be
      // isolated per env — its real characters 404 against the e2e backend. Serve a
      // deterministic per-env subset from the env's Typesense sidecar instead,
      // shaped like a Recombee response so the "For You" UI is unchanged. The lazy
      // import keeps this (and its query code) out of the production bundle.
      if (import.meta.env.MODE === "e2e" && typesenseConfig?.apiKeyPublicCharacter && typesenseConfig?.collectionNamePublicCharacter) {
        const { default: fetchE2eRecommendations } = await import("@/hooks/e2eRecommendations");
        return fetchE2eRecommendations({
          count: recommendationsCount,
          apiKey: typesenseConfig.apiKeyPublicCharacter,
          collection: typesenseConfig.collectionNamePublicCharacter,
        });
      }
      const response = await client.send(
        new recombee.RecommendItemsToUser(userId, recommendationsCount, {
          filter,
          cascadeCreate: true,
          scenario,
          returnProperties,
        }),
      );
      return response as unknown as IRecommItems;
    },
    [typesenseConfig],
  );

  const getCachedRecommendations = useCallback(
    (params: IGetCachedRecommendationsParams) =>
      loadCachedRecommendations({ ...params, fetchRecommendations: getRecommendations, onError: errorHandler }),
    [getRecommendations, errorHandler],
  );

  return {
    sendUserInteractionDetailView,
    getRecommendations,
    getCachedRecommendations,
    sendUserInteractionCartAddition,
    sendUserInteractionRating,
    sendUserInteractionAddBookmark,
    sendUserInteractionViewPortion,
    recombeeUserId,
  };
}

export default useRecombee;
